import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../components/Admin/AdminContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Tag, Hash, Search, ChevronDown, ChevronRight, X,
  MapPin, Clock, Sun, Cloud, Users, Footprints, Filter,
  BarChart3, TrendingUp, Eye
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminTags() {
  const { adminUser } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tagsData, setTagsData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selectedTag, setSelectedTag] = useState(null);
  const [spotsDialogOpen, setSpotsDialogOpen] = useState(false);
  const [tagSpots, setTagSpots] = useState([]);
  const [loadingSpots, setLoadingSpots] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'regular', 'system'

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${BACKEND_URL}/api/admin/tags/all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTagsData(data);
      } else {
        setError('Erreur lors du chargement des tags');
      }
    } catch (error) {
      console.error('Failed to load tags:', error);
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const loadSpotsByTag = async (tag, isSystem = false) => {
    try {
      setLoadingSpots(true);
      setSelectedTag(tag);
      setSpotsDialogOpen(true);

      const token = localStorage.getItem('adminToken');
      let url;
      
      if (isSystem) {
        const [categoryId, value] = tag.id.split(':');
        url = `${BACKEND_URL}/api/admin/tags/system/${categoryId}/${value}/spots`;
      } else {
        url = `${BACKEND_URL}/api/admin/tags/regular/${tag.slug}/spots`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTagSpots(data.spots || []);
      } else {
        setTagSpots([]);
      }
    } catch (error) {
      console.error('Failed to load spots:', error);
      setTagSpots([]);
    } finally {
      setLoadingSpots(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const filteredRegularTags = tagsData?.regular_tags?.filter(tag => 
    tag.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tag.slug?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const filteredSystemTags = tagsData?.system_tags?.filter(tag =>
    tag.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tag.value?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  // Grouper les tags système par catégorie
  const systemTagsByCategory = filteredSystemTags.reduce((acc, tag) => {
    if (!acc[tag.category_id]) {
      acc[tag.category_id] = {
        name: tag.category_name,
        icon: tag.category_icon,
        color: tag.category_color,
        tags: []
      };
    }
    acc[tag.category_id].tags.push(tag);
    return acc;
  }, {});

  // Grouper les tags réguliers par catégorie
  const regularTagsByCategory = filteredRegularTags.reduce((acc, tag) => {
    const catId = tag.category_id || 'other';
    if (!acc[catId]) {
      acc[catId] = {
        name: tag.category_name || 'Autre',
        icon: tag.category_icon || 'Tag',
        color: tag.category_color || '#64748b',
        tags: []
      };
    }
    acc[catId].tags.push(tag);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-600">Chargement des tags...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Tag className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 font-medium">{error}</p>
          <Button onClick={loadTags} variant="outline" className="mt-4">
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestion des Tags</h1>
          <p className="text-gray-600 mt-1">Visualisation et statistiques des tags</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Tags réguliers</p>
                <p className="text-2xl font-bold">{tagsData?.stats?.total_regular_tags || 0}</p>
              </div>
              <Hash className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Tags système</p>
                <p className="text-2xl font-bold">{tagsData?.stats?.total_system_tags || 0}</p>
              </div>
              <Filter className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Catégories</p>
                <p className="text-2xl font-bold">
                  {(tagsData?.stats?.total_categories || 0) + (tagsData?.stats?.total_system_categories || 0)}
                </p>
              </div>
              <Tag className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Tag le + utilisé</p>
                <p className="text-lg font-bold truncate">
                  {tagsData?.stats?.most_used_regular?.[0]?.name || '-'}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Tabs */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div className="relative w-full md:w-96">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Rechercher un tag..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={activeTab === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('all')}
          >
            Tous
          </Button>
          <Button
            variant={activeTab === 'system' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('system')}
          >
            <Filter className="h-4 w-4 mr-1" />
            Système ({tagsData?.stats?.total_system_tags || 0})
          </Button>
          <Button
            variant={activeTab === 'regular' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('regular')}
          >
            <Hash className="h-4 w-4 mr-1" />
            Réguliers ({tagsData?.stats?.total_regular_tags || 0})
          </Button>
        </div>
      </div>

      {/* System Tags Section */}
      {(activeTab === 'all' || activeTab === 'system') && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-purple-500" />
              Tags Système (Métadonnées V2)
            </CardTitle>
            <CardDescription>
              Tags automatiques basés sur les caractéristiques des spots
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(systemTagsByCategory).map(([categoryId, category]) => (
                <div key={categoryId} className="border rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleCategory(`system-${categoryId}`)}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{category.icon}</span>
                      <span className="font-medium">{category.name}</span>
                      <Badge variant="secondary" className="ml-2">
                        {category.tags.length} tags
                      </Badge>
                    </div>
                    {expandedCategories[`system-${categoryId}`] ? (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  
                  {expandedCategories[`system-${categoryId}`] && (
                    <div className="p-3 bg-white">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-2 px-3">Icône</th>
                              <th className="text-left py-2 px-3">ID Système</th>
                              <th className="text-left py-2 px-3">Nom affiché</th>
                              <th className="text-center py-2 px-3">Utilisations</th>
                              <th className="text-center py-2 px-3">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {category.tags.map((tag) => (
                              <tr key={tag.id} className="border-b hover:bg-gray-50">
                                <td className="py-2 px-3">
                                  <span className="text-xl">{tag.icon}</span>
                                </td>
                                <td className="py-2 px-3">
                                  <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                                    {tag.value}
                                  </code>
                                </td>
                                <td className="py-2 px-3 font-medium">{tag.name}</td>
                                <td className="py-2 px-3 text-center">
                                  <Badge 
                                    variant={tag.usage_count > 0 ? 'default' : 'secondary'}
                                    style={{ backgroundColor: tag.usage_count > 0 ? category.color : undefined }}
                                  >
                                    {tag.usage_count} spot{tag.usage_count !== 1 ? 's' : ''}
                                  </Badge>
                                </td>
                                <td className="py-2 px-3 text-center">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => loadSpotsByTag(tag, true)}
                                    disabled={tag.usage_count === 0}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Regular Tags Section */}
      {(activeTab === 'all' || activeTab === 'regular') && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Hash className="h-5 w-5 text-blue-500" />
              Tags Réguliers
            </CardTitle>
            <CardDescription>
              Tags créés par les utilisateurs pour catégoriser les spots
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(regularTagsByCategory).map(([categoryId, category]) => (
                <div key={categoryId} className="border rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleCategory(`regular-${categoryId}`)}
                    className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span 
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: category.color }}
                      />
                      <span className="font-medium">{category.name}</span>
                      <Badge variant="secondary" className="ml-2">
                        {category.tags.length} tags
                      </Badge>
                    </div>
                    {expandedCategories[`regular-${categoryId}`] ? (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  
                  {expandedCategories[`regular-${categoryId}`] && (
                    <div className="p-3 bg-white">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-2 px-3">ID Système (slug)</th>
                              <th className="text-left py-2 px-3">Nom affiché</th>
                              <th className="text-left py-2 px-3">Description</th>
                              <th className="text-center py-2 px-3">Utilisations</th>
                              <th className="text-center py-2 px-3">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {category.tags.map((tag) => (
                              <tr key={tag.id} className="border-b hover:bg-gray-50">
                                <td className="py-2 px-3">
                                  <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                                    {tag.slug}
                                  </code>
                                </td>
                                <td className="py-2 px-3">
                                  <Badge 
                                    style={{ 
                                      backgroundColor: `${category.color}20`,
                                      color: category.color,
                                      border: `1px solid ${category.color}40`
                                    }}
                                  >
                                    #{tag.name}
                                  </Badge>
                                </td>
                                <td className="py-2 px-3 text-gray-500 text-xs max-w-xs truncate">
                                  {tag.description || '-'}
                                </td>
                                <td className="py-2 px-3 text-center">
                                  <Badge variant={tag.usage_count > 0 ? 'default' : 'secondary'}>
                                    {tag.usage_count} spot{tag.usage_count !== 1 ? 's' : ''}
                                  </Badge>
                                </td>
                                <td className="py-2 px-3 text-center">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => loadSpotsByTag(tag, false)}
                                    disabled={tag.usage_count === 0}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Spots Dialog */}
      <Dialog open={spotsDialogOpen} onOpenChange={setSpotsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Spots avec le tag: {selectedTag?.icon} {selectedTag?.name}
            </DialogTitle>
          </DialogHeader>
          
          {loadingSpots ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : tagSpots.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Aucun spot trouvé avec ce tag
            </div>
          ) : (
            <div className="space-y-3">
              {tagSpots.map((spot) => (
                <div 
                  key={spot.id} 
                  className="flex items-center gap-4 p-3 border rounded-lg hover:bg-gray-50"
                >
                  {/* Photo */}
                  <div className="w-20 h-20 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden">
                    {spot.photos?.[0]?.url || spot.photos?.[0]?.cdn_url ? (
                      <img 
                        src={spot.photos[0].url || spot.photos[0].cdn_url} 
                        alt={spot.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <MapPin className="h-8 w-8 text-gray-300" />
                      </div>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 truncate">{spot.title}</h4>
                    <p className="text-sm text-gray-500 truncate">{spot.description}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">
                        ID: {spot.id?.slice(0, 8)}...
                      </Badge>
                      {spot.author && (
                        <span className="text-xs text-gray-400">
                          Par {spot.author}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Stats */}
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>❤️ {spot.likes || spot.likes_count || 0}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
